import { createMutation } from "@evergis/react-fetching";
import { ResourceTypeLink } from "@evergis/api";
import { api as evergisApi } from "../evergis/api";
import { ResourceService } from "../interfaces/service";
import { project, projects } from "./projects";
import { invalidateLayer, invalidateTable } from "./invalidations";
import { getFileBySrc } from "../utils/image";

type PreviewPayload = {
  name: string;
  preview: File;
};

export const getPreview = (type: ResourceTypeLink, resourceName: string) => {
  switch (type) {
    case ResourceTypeLink.Project:
      return evergisApi.projects.getPreview(resourceName);
    case ResourceTypeLink.Layer:
      return evergisApi.layers.getPreview(resourceName);
    case ResourceTypeLink.Table:
      return evergisApi.tables.getPreview(resourceName);
    default:
      return "";
  }
};

async function updatePreview(service: ResourceService, { name, preview }: PreviewPayload) {
  let fileId = "";

  if (preview) {
    const uploadResult = await service.uploadPreview(name, preview);

    fileId = uploadResult.fileId;
  } else {
    await service.deletePreview(name);
  }

  return {
    name,
    fileId,
  };
}

export const setProjectPreview = createMutation(
  (payload: PreviewPayload) => async api => ({
    data: await updatePreview(api.projects, payload),
  }),
  {
    injected: evergisApi,
    update: result => {
      project.write({
        input: result.name,
        reducer: value => ({ ...value, preview: result.fileId }),
      });

      projects.invalidateAll();
    },
  },
);

export const setLayerPreview = createMutation(
  (payload: PreviewPayload) => async api => ({
    data: await updatePreview(api.layers, payload),
  }),
  {
    injected: evergisApi,
    update: ({ name }) => invalidateLayer(name),
  },
);

export const setTablePreview = createMutation(
  (payload: PreviewPayload) => async api => ({
    data: await updatePreview(api.tables, payload),
  }),
  {
    injected: evergisApi,
    update: ({ name }) => invalidateTable(name),
  },
);

export const copyProjectPreview = createMutation(
  ({ from, to }: { from: string; to: string }) =>
    async api => {
      const previewSrc = api.projects.getPreview(from);

      const preview = await getFileBySrc(previewSrc, `${to}.png`);

      return {
        data: await updatePreview(api.layers, { name: to, preview }),
      };
    },
  {
    injected: evergisApi,
  },
);
